import { DocumentResponse } from '../services/apiService';

interface CitationProcessingResult {
  processedContent: string;
  documentMap: Map<number, number>; // original doc number -> normalized doc number
  referencedDocuments: DocumentResponse[];
}

/**
 * Processes LLM response content to normalize document citations and convert them to markdown
 * 
 * @param content - The LLM response content containing [Document X, Document Y] references
 * @param documents - Array of documents from the backend
 * @returns Processed content with normalized citations and referenced documents
 */
export function processCitations(content: string, documents: DocumentResponse[]): CitationProcessingResult {
  if (!content || !documents || documents.length === 0) {
    return {
      processedContent: content,
      documentMap: new Map(),
      referencedDocuments: []
    };
  }

  // Find all document references in the content in order of appearance
  // Updated regex to handle citations with additional info like "section 5.2"
  const citationRegex = /\[Document\s+\d+(?:[^\]]*?)\]/g;
  const documentNumberRegex = /Document\s+(\d+)/g;
  
  const referencedDocNumbers: number[] = [];
  const seenDocNumbers = new Set<number>();
  
  // Extract all referenced document numbers in order of first appearance
  let match;
  while ((match = citationRegex.exec(content)) !== null) {
    const fullMatch = match[0];
    let docMatch;
    while ((docMatch = documentNumberRegex.exec(fullMatch)) !== null) {
      const docNum = parseInt(docMatch[1], 10);
      // Only add if we haven't seen this document number before
      if (!seenDocNumbers.has(docNum)) {
        referencedDocNumbers.push(docNum);
        seenDocNumbers.add(docNum);
      }
    }
    // Reset regex for next iteration
    documentNumberRegex.lastIndex = 0;
  }

  // Create mapping from original document numbers to normalized sequential numbers
  // Based on order of first appearance, not numerical order
  const documentMap = new Map<number, number>();
  const referencedDocuments: DocumentResponse[] = [];

  referencedDocNumbers.forEach((originalDocNum, index) => {
    const normalizedDocNum = index + 1;
    documentMap.set(originalDocNum, normalizedDocNum);
    
    // Find the corresponding document (documents are 0-indexed, but referenced as 1-indexed)
    const documentIndex = originalDocNum - 1;
    if (documentIndex >= 0 && documentIndex < documents.length) {
      referencedDocuments.push(documents[documentIndex]);
    }
  });

  // Process the content to replace citations with markdown links
  let processedContent = content;
  
  // Reset regex
  citationRegex.lastIndex = 0;
  
  // Replace each citation with markdown format
  processedContent = processedContent.replace(citationRegex, (fullMatch) => {
    const docNumbers: number[] = [];
    let docMatch;
    
    // Reset regex for this match
    documentNumberRegex.lastIndex = 0;
    
    while ((docMatch = documentNumberRegex.exec(fullMatch)) !== null) {
      const docNum = parseInt(docMatch[1], 10);
      docNumbers.push(docNum);
    }
    
    // Extract additional citation info (like "section 5.2")
    const additionalInfo = extractAdditionalCitationInfo(fullMatch);
    
    // Convert to normalized numbers and create markdown links with document names and URLs
    const normalizedRefs = docNumbers
      .map(docNum => {
        const normalizedNum = documentMap.get(docNum);
        if (normalizedNum !== undefined) {
          const documentIndex = normalizedNum - 1;
          const doc = referencedDocuments[documentIndex];
          const docName = doc?.document || `Document ${normalizedNum}`;
          
          // Determine the URL based on document type
          let documentUrl = '';
          if (doc?.file) {
            // For documents with file field (Example Policies), use the document endpoint
            documentUrl = `/api/documents/${encodeURIComponent(doc.file)}`;
          } else if (doc?.source_url) {
            // For other documents with source_url, use the source_url
            documentUrl = doc.source_url;
          }
          
          // Include document URL in the title attribute for the Citation component to access
          const titleWithUrl = documentUrl ? `${docName}|${documentUrl}` : docName;
          
          // Create citation text with additional info if present
          const citationText = additionalInfo ? `${normalizedNum}, ${additionalInfo}` : `${normalizedNum}`;
          return `[${citationText}](#doc-${normalizedNum} "${titleWithUrl}")`;
        }
        return null;
      })
      .filter(ref => ref !== null)
      .join(', ');
    
    return normalizedRefs || fullMatch; // Fallback to original if something goes wrong
  });

  return {
    processedContent,
    documentMap,
    referencedDocuments
  };
}

/**
 * Extracts additional citation information (like "section 5.2") from a citation
 */
function extractAdditionalCitationInfo(citation: string): string {
  // Remove the brackets and extract everything after the document number(s)
  const withoutBrackets = citation.slice(1, -1); // Remove [ and ]
  
  // Find the last Document reference and get everything after it
  const lastDocumentMatch = withoutBrackets.match(/.*Document\s+\d+(.*)$/);
  if (lastDocumentMatch && lastDocumentMatch[1]) {
    const additionalInfo = lastDocumentMatch[1].trim();
    // Remove leading comma if present
    return additionalInfo.replace(/^,\s*/, '');
  }
  
  return '';
}

/**
 * Removes document citations from content (for streaming display)
 */
export function removeDocumentCitations(content: string): string {
  if (!content) return content;
  
  // Remove [Document X, Document Y] patterns including additional info
  const citationRegex = /\[Document\s+\d+(?:[^\]]*?)\]/g;
  return content.replace(citationRegex, '').trim();
}

/**
 * Checks if content contains document citations
 */
export function hasDocumentCitations(content: string): boolean {
  const citationRegex = /\[Document\s+\d+/;
  return citationRegex.test(content);
}

/**
 * Extracts document numbers from citation text
 */
export function extractDocumentNumbers(citationText: string): number[] {
  const documentNumberRegex = /Document\s+(\d+)/g;
  const numbers: number[] = [];
  let match;
  
  while ((match = documentNumberRegex.exec(citationText)) !== null) {
    numbers.push(parseInt(match[1], 10));
  }
  
  return numbers;
}
